import React, { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import AuthenticatedLayout from '../../components/layouts/authenticated_layout'
import PartnerRecruitmentManageCard from '../../components/cards/partner_recruitment_manage_card'
import { useAllOwnRecruitment } from '../../hooks/use_all_own_recruitment'
import { RecruitmentStatusValue } from '../../enums/RecruitmentStatus'
import { useInfiniteScroll } from '../../hooks/use_infinite_scroll'
import LoadingSpinner from '../../components/loading/loading_spinner'
import Breadcrumb from '../../components/common/breadcrumb'

/**
 * 投稿済み一覧ページ
 * 自社の募集（公開・下書き・予約）を全て表示し、編集機能を提供
 */
const RecruitmentManagePage: React.FC = () => {
  const navigate = useNavigate()

  // 自社の全募集データ取得（ステータス問わず）
  const { isLoading, isLoadingMore, hasMore, recruitments, loadMore } =
    useAllOwnRecruitment()

  const { LoadingElement } = useInfiniteScroll(loadMore, isLoadingMore, hasMore)

  // 選択中のフィルター
  const [filter, setFilter] = useState<RecruitmentStatusValue | 'all'>('all')

  // フィルタリングされた募集一覧
  const filteredRecruitments = useMemo(() => {
    if (filter === 'all') {
      return recruitments
    } else {
      return recruitments.filter((rec) => rec.status === filter)
    }
  }, [filter, recruitments])

  // 新規作成ボタンクリック時の処理
  const handleCreateClick = () => {
    navigate('/recruitment/create')
  }

  return (
    <AuthenticatedLayout>
      <div className="container mx-auto px-4 py-8">
        <Breadcrumb
          items={[
            { label: 'ホーム', path: '/dashboard' },
            { label: '自社の募集' },
          ]}
        />

        {/* ヘッダー部分 */}
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">自社の募集</h1>
          <button
            className="bg-main-green hover:bg-main-lightgreen text-white font-bold py-2 px-4 rounded flex items-center gap-2 transition-colors"
            onClick={handleCreateClick}
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
            <span>新規募集を作成</span>
          </button>
        </div>

        {/* フィルターボタン */}
        <div className="flex gap-2 mb-6">
          <button
            className={`px-4 py-2 rounded ${
              filter === 'all'
                ? 'bg-main-green text-white'
                : 'bg-main-white text-main-green border border-main-green hover:bg-main-lightgreen hover:text-white'
            }`}
            onClick={() => setFilter('all')}
          >
            すべて ({recruitments.length})
          </button>
          <button
            className={`px-4 py-2 rounded ${
              filter === 'published'
                ? 'bg-main-green text-white'
                : 'bg-main-white text-main-green border border-main-green hover:bg-main-lightgreen hover:text-white'
            }`}
            onClick={() => setFilter('published')}
          >
            公開中 (
            {recruitments.filter((r) => r.status === 'published').length})
          </button>
          <button
            className={`px-4 py-2 rounded ${
              filter === 'draft'
                ? 'bg-main-green text-white'
                : 'bg-main-white text-main-green border border-main-green hover:bg-main-lightgreen hover:text-white'
            }`}
            onClick={() => setFilter('draft')}
          >
            下書き ({recruitments.filter((r) => r.status === 'draft').length})
          </button>
          <button
            className={`px-4 py-2 rounded ${
              filter === 'scheduled'
                ? 'bg-main-green text-white'
                : 'bg-main-white text-main-green border border-main-green hover:bg-main-lightgreen hover:text-white'
            }`}
            onClick={() => setFilter('scheduled')}
          >
            予約投稿 (
            {recruitments.filter((r) => r.status === 'scheduled').length})
          </button>
        </div>

        {/* 募集一覧グリッド */}
        {isLoading && recruitments.length === 0 ? (
          <div className="flex justify-center py-20">
            <LoadingSpinner />
          </div>
        ) : filteredRecruitments.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-4 gap-6 mb-12">
            {filteredRecruitments.map((recruitment, index) => (
              <PartnerRecruitmentManageCard
                key={`${recruitment.id}-${index}`}
                recruitment={recruitment}
                initialized={!isLoading}
              />
            ))}
            {hasMore && LoadingElement}
          </div>
        ) : (
          <div className="text-center py-12">
            <p className="text-gray-500 mb-4">
              {filter === 'all'
                ? '募集がまだありません'
                : `${
                    filter === 'published'
                      ? '公開中'
                      : filter === 'draft'
                        ? '下書き'
                        : '予約投稿'
                  }の募集はありません`}
            </p>
            {filter === 'all' && (
              <button
                className="bg-main-green hover:bg-main-lightgreen text-white font-bold py-2 px-4 rounded flex items-center gap-2 transition-colors mx-auto"
                onClick={handleCreateClick}
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4v16m8-8H4"
                  />
                </svg>
                <span>募集を作成する</span>
              </button>
            )}
          </div>
        )}
      </div>
    </AuthenticatedLayout>
  )
}

export default RecruitmentManagePage
